import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {TransportEntity} from "@ots-frontend/ots-data";
import {IconDownload, IconEdit, IconTrash} from "@tabler/icons-react";
import {useTransportAction} from "./useTransportAction";

const ALL_TRANSPORT_ACTIONS = [
  "EDIT",
  "DELETE",
  "DOWNLOAD",
] as const;

export type TransportAction = typeof ALL_TRANSPORT_ACTIONS[number];

export type TransportActionMenuDropdownProps = {
  transport: TransportEntity
  actions?: TransportAction[]
  onActionClick?: (action: TransportAction) => void
  showEmpty?: boolean
}

export const TransportActionMenuDropdown = (props: TransportActionMenuDropdownProps) => {
  const {
    transport,
    actions = ALL_TRANSPORT_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<TransportAction> = useMemo(() => {
    const enabledActions = new Set<TransportAction>()
    const actionsSet = actions ? new Set<TransportAction>(actions) : new Set<TransportAction>()

    if (hasAccess("transport_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("transport_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    if (actionsSet.has("DOWNLOAD")) {
      enabledActions.add("DOWNLOAD")
    }

    return enabledActions
  }, [transport, actions, hasAccess])

  const {
    onTransportActionClick
  } = useTransportAction()

  const _onActionClick = useCallback((action: TransportAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onTransportActionClick(transport, action)
    }
  }, [onActionClick, onTransportActionClick, transport])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DOWNLOAD") &&
        <Menu.Item
          onClick={() => _onActionClick("DOWNLOAD")}
          icon={<IconDownload size={14}/>}>
          {t("common.action.download", "Download")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
